import React, { useState } from 'react';
import { translate } from '../../../services/translate';
import { GetCamerasMapParameters } from '../../../services/central-api/cameras';
import Filters from '../../../components/Filter';
import FilterCheckBox from '../../../components/Filter/components/FilterCheckBox';
import { View } from 'react-native';
import MyAppText from '../../../components/MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import PolygonByTypeDropdown from '../../../components/PolygonByTypeDropdown';
import PolygonTypeDropdown from '../../../components/PolygonTypeDropdown';

interface Props {
    setFilters: React.Dispatch<React.SetStateAction<GetCamerasMapParameters>>;
    filters: GetCamerasMapParameters;
    showMapPoi: boolean;
    setShowMapPoi: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function MapFilter({
    setFilters,
    filters,
    showMapPoi,
    setShowMapPoi,
}: Props) {
    const { styles } = useStyles(styleSheet);

    const [isLpr, setIsLpr] = useState<boolean>(filters.types?.includes('lpr') ? true : false);
    const [isPtz, setIsPtz] = useState<boolean>(filters.types?.includes('ptz') ? true : false);
    const [isPinned, setIsPinned] = useState<boolean>(filters.types?.includes('pinned') ? true : false);
    const [isPanoramic, setIsPanoramic] = useState<boolean>(filters.types?.includes('panoramic') ? true : false);
    const [isType1, setIsType1] = useState<boolean>(filters.integrationType?.includes('type_1') ? true : false);
    const [isType2, setIsType2] = useState<boolean>(filters.integrationType?.includes('type_2') ? true : false);
    const [isType3, setIsType3] = useState<boolean>(filters.integrationType?.includes('type_3') ? true : false);
    const [isOnline, setIsOnline] = useState<boolean>(filters.online || false);
    const [isOffline, setIsOffline] = useState<boolean>(filters.offline || false);
    const [isPoiVisible, setIsPoiVisible] = useState<boolean>(showMapPoi);
    const [polygonPendingLoading, setPolygonPendingLoading] = useState<boolean>(false);
    const [selectedPolygon, setSelectedPolygon] = useState<number>(filters.polygon || 0);
    const [selectedPolygonType, setSelectedPolygonType] = useState<string>(filters.polygonType || '');

    function toggleFilter<T extends string>(
        arr: T[] | undefined,
        value: T,
        enabled: boolean
    ) {
        if (!arr) {
            return;
        }
        const index = arr.indexOf(value);
        if (enabled && index === -1) {
            arr.push(value);
        } else if (!enabled && index !== -1) {
            arr.splice(index, 1);
        }
    }

    const handleFilter = () => {
        toggleFilter(filters.types, 'lpr', isLpr);
        toggleFilter(filters.types, 'ptz', isPtz);
        toggleFilter(filters.types, 'pinned', isPinned);
        toggleFilter(filters.types, 'panoramic', isPanoramic);
        toggleFilter(filters.integrationType, 'type_1', isType1);
        toggleFilter(filters.integrationType, 'type_2', isType2);
        toggleFilter(filters.integrationType, 'type_3', isType3);

        setFilters({
            ...filters,
            polygon: selectedPolygon != 0 ? selectedPolygon : undefined,
            types: filters.types,
            integrationType: filters.integrationType,
            online: isOnline,
            offline: isOffline
        });
        setShowMapPoi(isPoiVisible);
    };

    const handleCleanFilter = () => {
        setIsLpr(false);
        setIsPtz(false);
        setIsPinned(false);
        setIsPanoramic(false);
        setIsType1(false);
        setIsType2(false);
        setIsType3(false);
        setIsOnline(false);
        setIsOffline(false);
        setIsPoiVisible(false);
        setSelectedPolygonType('');
        setSelectedPolygon(0);
    };

    return (
        <Filters
            onCleanFilters={handleCleanFilter}
            onFilters={handleFilter}
            applyIsLoading={polygonPendingLoading}
            applyDisabled={selectedPolygonType != '' && selectedPolygon == 0}
        >
            <View style={styles.section(5)}>
                <MyAppText style={styles.sectionTitle}>{translate('cameraType')}</MyAppText>
                <View style={styles.checkBoxContainer}>
                    <FilterCheckBox label={translate('lpr')}
                        value={isLpr}
                        onChange={setIsLpr}
                        showTooltip
                        tooltipText={translate('lprInfo')}
                        zIndex={4}
                    />
                    <FilterCheckBox
                        label={translate('ptz')}
                        value={isPtz}
                        onChange={setIsPtz}
                        showTooltip
                        tooltipText={translate('ptzInfo')}
                        zIndex={3}
                    />
                    <FilterCheckBox
                        label={translate('pinned')}
                        value={isPinned}
                        onChange={setIsPinned}
                        showTooltip
                        tooltipText={translate('pinnedInfo')}
                        zIndex={2}
                    />
                    <FilterCheckBox
                        label={translate('panoramic')}
                        value={isPanoramic}
                        onChange={setIsPanoramic}
                        showTooltip
                        tooltipText={translate('panoramicInfo')}
                        zIndex={1}
                    />
                </View>
            </View>

            <View style={styles.section(4)}>
                <MyAppText style={styles.sectionTitle}>{translate('integrationType')}</MyAppText>
                <View style={[styles.checkBoxContainer]}>
                    <FilterCheckBox
                        label={translate('type_1')}
                        value={isType1}
                        onChange={setIsType1}
                        showTooltip
                        tooltipText={translate('type1Info')}
                        zIndex={4}
                    />
                    <FilterCheckBox
                        label={translate('type_2')}
                        value={isType2}
                        onChange={setIsType2}
                        showTooltip
                        tooltipText={translate('type2Info')}
                        zIndex={3}
                    />
                    <FilterCheckBox
                        label={translate('type_3')}
                        value={isType3}
                        onChange={setIsType3}
                        showTooltip
                        tooltipText={translate('type3Info')}
                        zIndex={2}
                    />
                </View>
            </View>
            <View style={styles.section(3)}>
                <MyAppText style={styles.sectionTitle}>{translate('cameraStatus')}</MyAppText>
                <View style={styles.checkBoxContainer}>
                    <FilterCheckBox
                        label={translate('online')}
                        value={isOnline}
                        onChange={setIsOnline}
                        showTooltip
                        tooltipText={translate('onlineInfo')}
                        zIndex={3}
                    />
                    <FilterCheckBox
                        label={translate('offline')}
                        value={isOffline}
                        onChange={setIsOffline}
                        showTooltip
                        tooltipText={translate('offlineInfo')}
                        zIndex={2}
                    />
                </View>
            </View>
            <View style={styles.section(2)}>
                <MyAppText style={styles.sectionTitle}>{translate('mapConfigurations')}</MyAppText>
                <View style={[styles.checkBoxContainer, { zIndex: 4 }]}>
                    <FilterCheckBox
                        label={translate('pointsOfInterest')}
                        value={isPoiVisible}
                        onChange={setIsPoiVisible}
                        showTooltip
                        tooltipText={translate('poiInfo')}
                    />
                </View>
                <View style={{ zIndex: 3, paddingLeft: 15, gap: 5 }}>
                    <PolygonTypeDropdown
                        label={translate('regions')}
                        dropDownDirection='BOTTOM'
                        showTooltip
                        tooltipText={translate('regionsInfo')}
                        zIndex={3}
                        setPolygonType={setSelectedPolygonType}
                        polygonType={selectedPolygonType}
                    />
                    {selectedPolygonType != '' &&
                        <PolygonByTypeDropdown
                            label={translate('subRegions')}
                            dropDownDirection='BOTTOM'
                            setPolygon={setSelectedPolygon}
                            polygon={selectedPolygon}
                            polygonType={selectedPolygonType}
                            showTooltip
                            tooltipText={translate('subRegionsInfo')}
                            zIndex={2}
                            setPendingLoading={setPolygonPendingLoading}
                        />
                    }
                </View>
            </View>
        </Filters>
    );
}

const styleSheet = createStyleSheet(() => ({
    section: (zIndex: number) => {
        return {
            gap: 5,
            marginBottom: 10,
            zIndex
        };
    },
    sectionTitle: {
        fontSize: 16,
    },
    checkBoxContainer: {
        flexDirection: 'column',
        paddingLeft: 15,
        gap: 5,
    },
    mapPolygonDropdown: {
        zIndex: 12,
    },
}));
